import { useTranslation } from 'react-i18next'
import { CreditCard, QrCode, Receipt, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { PaymentMethod } from '@/types/checkout'

const ICONS: Record<PaymentMethod, LucideIcon> = {
  PIX: QrCode,
  CREDIT_CARD: CreditCard,
  BOLETO: Receipt
}

interface PaymentMethodSelectorProps {
  /** Only the methods the product allows; the order is the order shown. */
  methods: PaymentMethod[]
  value: PaymentMethod | null
  onChange: (method: PaymentMethod) => void
  disabled?: boolean
  /** Shown but not choosable yet (F-195: PIX and boleto on a plan change), with the reason as their title. */
  unavailable?: PaymentMethod[]
  unavailableHint?: string
  className?: string
}

/**
 * How to pay, as a row of equal options.
 *
 * A single allowed method is still shown — as the only option, already
 * chosen — so the buyer knows how they are about to pay before pressing the
 * button.
 */
export function PaymentMethodSelector({ methods, value, onChange, disabled, unavailable = [], unavailableHint, className }: PaymentMethodSelectorProps) {
  const { t } = useTranslation('checkout')

  return (
    <div role="radiogroup" aria-label={t('purchase.paymentMethod')} className={cn('flex gap-2', className)}>
      {/* Only the methods this selector knows: an unknown one from the API is left out, never a broken button. */}
      {methods.filter((method) => method in ICONS).map((method) => {
        const Icon = ICONS[method]
        const selected = value === method
        const soon = unavailable.includes(method)
        return (
          <button
            key={method}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={disabled || soon}
            aria-disabled={soon || undefined}
            title={soon ? unavailableHint : undefined}
            data-method={method}
            onClick={() => onChange(method)}
            className={cn(
              'flex min-w-0 flex-1 items-center justify-center gap-1.5 rounded-lg border-2 bg-white px-3 py-2.5 text-sm font-medium transition-colors disabled:opacity-50',
              selected ? 'border-primary text-primary' : 'border-gray-200 text-gray-700 hover:border-gray-300'
            )}
          >
            <Icon className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">{t(`purchase.methods.${method}`)}</span>
          </button>
        )
      })}
    </div>
  )
}
